import { DOCUMENT } from '@angular/common';
import { effect, inject, Injectable, Injector } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { PackageFeature } from '@definitions/subscription.interface';
import { environment } from '@env/environment';
import { AuthService, User } from '@services/auth.service';
import { ConsentService } from '@services/consent.service';
import { POSTHOG_EVENTS, PosthogEvent } from '@services/posthog-events';
import { filter } from 'rxjs/operators';

/* eslint-disable @typescript-eslint/naming-convention -- PostHog SDK usa snake_case en su API pública */
type PosthogClient = {
  init: (apiKey: string, config: Record<string, unknown>) => void;
  capture: (eventName: string, properties?: Record<string, unknown>) => void;
  identify: (distinctId: string, properties?: Record<string, unknown>) => void;
  group: (groupType: string, groupKey: string, properties?: Record<string, unknown>) => void;
  register: (properties: Record<string, unknown>) => void;
  reset: () => void;
  get_distinct_id: () => string | undefined;
  opt_in_capturing: () => void;
  opt_out_capturing: () => void;
  has_opted_in_capturing: () => boolean;
};
/* eslint-enable @typescript-eslint/naming-convention */

type PosthogUser = User & {
  company?: {
    id: number;
    name?: string | null;
  };
  companyId?: number;
};

type QueuedCapture = {
  eventName: PosthogEvent;
  properties: Record<string, unknown>;
};

const ENVIRONMENT_ALIASES: Record<string, string> = {
  dev: 'development',
  preprod: 'preproduction',
  prod: 'production'
};
const VALID_ENVIRONMENTS = new Set(['local', 'development', 'uat', 'preproduction', 'production']);

declare global {
  interface Window {
    posthog?: PosthogClient;
  }
}

@Injectable({
  providedIn: 'root'
})
export class PosthogService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly consentService = inject(ConsentService);
  private readonly injector = inject(Injector);

  private hasStartedSync = false;
  private hasStartedRouteTracking = false;
  private hasTrackedInitialPage = false;
  private initializedClient: PosthogClient | null = null;
  private loadPromise: Promise<PosthogClient | null> | null = null;
  private lastIdentifiedUserKey: string | null = null;
  private readonly maxPreConsentCaptureQueueSize = 50;
  private readonly replayablePreConsentEvents = new Set<PosthogEvent>([
    POSTHOG_EVENTS.invitationPageViewed,
    POSTHOG_EVENTS.invitationEmailLinkViewed,
    POSTHOG_EVENTS.invitationLinkInvalid,
    POSTHOG_EVENTS.invitationCompanyLoaded,
    POSTHOG_EVENTS.invitationCompanyLoadFailed,
    POSTHOG_EVENTS.invitationAcceptFailed
  ]);
  private preConsentCaptureQueue: QueuedCapture[] = [];

  constructor() {
    this.startConsentSync();
    this.startRouteTracking();
  }

  async capture(eventName: PosthogEvent, properties: Record<string, unknown> = {}): Promise<void> {
    const analyticsConsent = this.consentService.analyticsConsent();

    if (analyticsConsent !== 'accepted') {
      if (analyticsConsent === 'unset') {
        this.queuePreConsentCapture(eventName, properties);
      }

      return;
    }

    await this.captureAcceptedEvent(eventName, properties);
  }

  getDistinctId(): string | null {
    if (!this.consentService.isAnalyticsAccepted() || !this.initializedClient) {
      return null;
    }

    try {
      const distinctId = this.initializedClient.get_distinct_id();

      return typeof distinctId === 'string' ? distinctId : null;
    } catch {
      return null;
    }
  }

  private async captureAcceptedEvent(eventName: PosthogEvent, properties: Record<string, unknown> = {}): Promise<void> {
    const client = await this.ensureInitialized();

    if (!client) {
      return;
    }

    const currentUser = this.authService.user();
    if (currentUser) {
      this.identifyCurrentUser(currentUser);
    }

    this.runPosthogCall(() =>
      client.capture(eventName, {
        app: 'iahoy-front',
        environment: this.getAnalyticsEnvironment(),
        ...properties
      })
    );
  }

  private startConsentSync(): void {
    if (this.hasStartedSync || !this.isTrackingConfigured()) {
      return;
    }

    this.hasStartedSync = true;

    effect(
      () => {
        const analyticsConsent = this.consentService.analyticsConsent();
        const currentUser = this.authService.user();

        if (analyticsConsent !== 'accepted') {
          this.hasTrackedInitialPage = false;
          this.lastIdentifiedUserKey = null;

          if (analyticsConsent === 'rejected') {
            this.preConsentCaptureQueue = [];
          }

          if (analyticsConsent === 'rejected' && this.initializedClient) {
            this.runPosthogCall(() => {
              this.initializedClient?.opt_out_capturing();
              this.initializedClient?.reset();
            });
          }

          return;
        }

        void this.syncAcceptedState(currentUser);
      },
      { injector: this.injector }
    );
  }

  private startRouteTracking(): void {
    if (this.hasStartedRouteTracking || !this.isTrackingConfigured()) {
      return;
    }

    this.hasStartedRouteTracking = true;

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        void this.capturePageView(event.urlAfterRedirects);
      });
  }

  private async syncAcceptedState(currentUser: User | null): Promise<void> {
    const client = await this.ensureInitialized();

    if (!client) {
      return;
    }

    if (!currentUser && this.lastIdentifiedUserKey) {
      this.resetIdentifiedUser(client);
    }

    const didOptIn = this.runPosthogCall(() => {
      client.opt_in_capturing();
      client.register({
        app: 'iahoy-front',
        environment: this.getAnalyticsEnvironment(),
        landing_url: environment.landingUrl
      });
    });

    if (!didOptIn) {
      return;
    }

    if (currentUser) {
      this.identifyCurrentUser(currentUser);
    }

    this.flushPreConsentCaptureQueue(client);

    if (!this.hasTrackedInitialPage) {
      await this.capturePageView(this.router.url);
      this.hasTrackedInitialPage = true;
    }
  }

  private async capturePageView(url: string): Promise<void> {
    if (!this.consentService.isAnalyticsAccepted()) {
      return;
    }

    const client = await this.ensureInitialized();

    if (!client) {
      return;
    }

    this.runPosthogCall(() =>
      client.capture('$pageview', {
        current_url: this.document.location?.href ?? window.location.href,
        path: url,
        app: 'iahoy-front',
        environment: this.getAnalyticsEnvironment()
      })
    );
  }

  private queuePreConsentCapture(eventName: PosthogEvent, properties: Record<string, unknown>): void {
    if (
      !this.isTrackingConfigured() ||
      !this.replayablePreConsentEvents.has(eventName) ||
      this.preConsentCaptureQueue.length >= this.maxPreConsentCaptureQueueSize
    ) {
      return;
    }

    this.preConsentCaptureQueue.push({
      eventName,
      properties: { ...properties }
    });
  }

  private flushPreConsentCaptureQueue(client: PosthogClient): void {
    if (this.preConsentCaptureQueue.length === 0) {
      return;
    }

    const queuedCaptures = this.preConsentCaptureQueue;
    this.preConsentCaptureQueue = [];

    for (const queuedCapture of queuedCaptures) {
      this.runPosthogCall(() =>
        client.capture(queuedCapture.eventName, {
          app: 'iahoy-front',
          environment: this.getAnalyticsEnvironment(),
          pre_consent_queued: true,
          ...queuedCapture.properties
        })
      );
    }
  }

  private identifyCurrentUser(currentUser: PosthogUser): void {
    const client = this.initializedClient;

    if (!client) {
      return;
    }

    const companyId = this.getCompanyId(currentUser);
    if (companyId === null) {
      return;
    }

    const userDistinctId = this.getUserDistinctId(currentUser.id);
    const companyGroupKey = this.getCompanyGroupKey(companyId);
    const identificationKey = [
      userDistinctId,
      companyId,
      currentUser.roles.join(','),
      this.authService.isImpersonating()
    ].join('|');

    if (this.lastIdentifiedUserKey === identificationKey) {
      return;
    }

    const companyName = this.getCompanyName(currentUser);
    const userType = this.authService.hasFeature(PackageFeature.AgencyWorkspace) ? 'gestoria' : 'client';

    const didIdentify = this.runPosthogCall(() => {
      client.identify(userDistinctId, {
        email: currentUser.email,
        name: currentUser.name,
        phone_number: currentUser.phone_number ?? '',
        role: currentUser.roles.join(','),
        user_type: userType,
        company_id: companyId,
        company_name: companyName,
        environment: this.getAnalyticsEnvironment(),
        is_impersonating: this.authService.isImpersonating()
      });
      client.group('company', companyGroupKey, {
        name: companyName,
        company_id: companyId,
        company_type: userType,
        environment: this.getAnalyticsEnvironment()
      });
      client.register({
        user_id: userDistinctId,
        company_id: companyId,
        // Mantenemos el nombre `company_distinct_id` como super property por
        // compatibilidad con queries existentes; el valor es el group key
        // (`company:<id>`) que PostHog usa para resolver el grupo.
        company_distinct_id: companyGroupKey,
        company_name: companyName,
        user_role: currentUser.roles.join(','),
        is_impersonating: this.authService.isImpersonating()
      });
    });

    if (!didIdentify) {
      return;
    }

    this.lastIdentifiedUserKey = identificationKey;
  }

  private resetIdentifiedUser(client: PosthogClient): void {
    const didReset = this.runPosthogCall(() => client.reset());

    if (didReset) {
      this.lastIdentifiedUserKey = null;
      this.hasTrackedInitialPage = false;
    }
  }

  private getUserDistinctId(userId: string | number): string {
    return `user:${userId}`;
  }

  /**
   * Devuelve el group key de PostHog para una company en formato `company:<id>`.
   * NO es un distinct_id (eso es para Persons); se usa con `client.group(...)`
   * y el backend lo replica vía `PosthogService._format_group_key` para que
   * front y backend apunten al mismo grupo.
   */
  private getCompanyGroupKey(companyId: string | number): string {
    return `company:${companyId}`;
  }

  private runPosthogCall(call: () => void): boolean {
    try {
      call();

      return true;
    } catch {
      return false;
    }
  }

  private async ensureInitialized(): Promise<PosthogClient | null> {
    if (!this.isTrackingConfigured()) {
      return null;
    }

    if (this.initializedClient) {
      return this.initializedClient;
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = new Promise<PosthogClient | null>((resolve) => {
      const posthogUrl = this.getPosthogScriptUrl();
      if (!posthogUrl) {
        return resolve(null);
      }

      const scriptId = 'posthog-js';
      if (this.document.getElementById(scriptId)) {
        const existingClient = window.posthog;
        if (!existingClient) {
          return resolve(null);
        }

        this.initializedClient = existingClient;
        this.initializeClient(existingClient);
        resolve(existingClient);

        return;
      }

      const script = this.document.createElement('script');
      script.id = scriptId;
      script.async = true;
      script.src = posthogUrl;
      script.onload = () => {
        const client = window.posthog;

        if (!client) {
          this.loadPromise = null;

          return resolve(null);
        }

        this.initializedClient = client;
        this.initializeClient(client);
        resolve(client);
      };
      script.onerror = () => {
        this.loadPromise = null;

        return resolve(null);
      };

      this.document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  private initializeClient(client: PosthogClient): void {
    const config = environment.posthog;
    if (!config?.apiKey) {
      return;
    }

    const apiKey = config.apiKey;

    this.runPosthogCall(() =>
      client.init(apiKey, {
        api_host: config.apiHost ?? 'https://eu.i.posthog.com',
        capture_pageview: false,
        capture_pageleave: false,
        autocapture: false,
        persistence: 'localStorage+cookie',
        cross_subdomain_cookie: true,
        cookie_domain: '.iahoy.es'
      })
    );
  }

  private getPosthogScriptUrl(): string | null {
    const config = environment.posthog;
    if (!config?.enabled || !config.apiKey) {
      return null;
    }

    const host = config.apiHost ?? 'https://eu.i.posthog.com';

    return `${host.replace(/\/$/, '')}/static/array.js`;
  }

  private isTrackingConfigured(): boolean {
    return Boolean(environment.posthog?.enabled && environment.posthog.apiKey);
  }

  private getCompanyId(currentUser: PosthogUser): number | null {
    if (currentUser.company?.id) {
      return currentUser.company.id;
    }

    return currentUser.companyId ?? null;
  }

  private getCompanyName(currentUser: PosthogUser): string {
    return currentUser.company?.name ?? '';
  }

  private getAnalyticsEnvironment(): string {
    const hostname = this.document.location?.hostname ?? window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'local';
    }

    const environmentName = environment.name.trim().toLowerCase();
    const normalizedEnvironment = ENVIRONMENT_ALIASES[environmentName] ?? environmentName;

    return VALID_ENVIRONMENTS.has(normalizedEnvironment) ? normalizedEnvironment : 'development';
  }
}
